import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { DateRange } from "react-date-range";
import { toast } from "react-toastify";
import config from "../../utils/config";
import { emailTemplateTypeOptions, getEmailTemplateTypeLabel } from "../../utils/email-template-types";

const numRows = 20;

function EmailLogs(props) {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [templateType, setTemplateType] = useState("");
  const [status, setStatus] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    setPage(0);
  }, [props.organization, startDate, endDate, templateType, status, email]); // eslint-disable-line

  useEffect(() => {
    fetchLogs();
  }, [props.organization, page, startDate, endDate, templateType, status, email]); // eslint-disable-line

  const buildQuery = () => {
    const params = new URLSearchParams();

    params.set("page", page + 1);
    params.set("size", numRows);

    if (email.trim() !== "") {
      params.set("email", email.trim());
    }

    if (templateType !== "") {
      params.set("templateType", templateType);
    }

    if (status !== "") {
      params.set("status", status);
    }

    if (startDate) {
      params.set("startDate", startDate);
    }

    if (endDate) {
      params.set("endDate", endDate);
    }

    return params.toString();
  };

  const fetchLogs = () => {
    if (!props.organization) {
      return;
    }

    const query = buildQuery();
    const endpoint = `${config.API_ENDPOINT}/email-settings/logs/${props.organization}`;
    const headers = { headers: { "x-access-token": localStorage.getItem("access-token") } };

    axios
      .get(`${endpoint}/search?${query}`, headers)
      .then((res) => {
        setLogs(res.data);
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });

    axios
      .get(`${endpoint}/count?${query}`, headers)
      .then((res) => {
        setTotalRows(res.data.count || 0);
      })
      .catch((err) => {
        switch (err.response?.status) {
          case 401:
            localStorage.removeItem("access-token");
            window.location.replace("/login");
            break;
          default:
            toast.error("An unexpected error has occurred");
            break;
        }
      });
  };

  const handleDateChange = (item) => {
    console.log(item?.selection);
    setStartDate(item?.selection.startDate ?? null);
    setEndDate(moment(item?.selection.endDate).endOf("day").toDate() ?? null);
  };

  const getStatusLabel = (value) => {
    switch (value) {
      case "sent":
        return "Success";
      case "failed":
        return "Failure";
      default:
        return value;
    }
  };

  const hasDateFilter = Boolean(startDate && endDate);
  const hasFilters = hasDateFilter || templateType !== "" || status !== "" || email.trim() !== "";

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setTemplateType("");
    setStatus("");
    setEmail("");
  };

  const getStatusChipColor = (value) => {
    switch (value) {
      case "sent":
        return "success";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Card sx={{ p: 1.5 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Email Logs
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Track sent and failed emails for the selected organization.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip label={`${totalRows} results`} color="primary" variant="outlined" />
            {hasFilters ? <Chip label="Filters active" color="secondary" variant="outlined" /> : null}
          </Box>
        </Box>

        <Accordion sx={{ mb: 2.5, borderRadius: 2, border: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600 }}>View Filters</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1.1fr 1fr" },
                gap: 2,
              }}
            >
              <Card>
                <CardContent>
                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>By Date</Typography>
                  {startDate && endDate ? (
                    <Button sx={{ mb: 1 }} onClick={() => handleDateChange(null)}>
                      Clear Selection
                    </Button>
                  ) : null}
                  <DateRange
                    editableDateInputs={false}
                    moveRangeOnFirstSelection={false}
                    ranges={[
                      {
                        startDate: startDate,
                        endDate: endDate,
                        key: "selection",
                      },
                    ]}
                    onChange={handleDateChange}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardContent>
                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>By Template Type</Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="template-type-label">Template Type</InputLabel>
                    <Select
                      label="Template Type"
                      labelId="template-type-label"
                      value={templateType}
                      onChange={(e) => setTemplateType(e.target.value)}
                    >
                      <MenuItem value={""}>All</MenuItem>
                      {emailTemplateTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>By Status</Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="status-label">Status</InputLabel>
                    <Select
                      label="Status"
                      labelId="status-label"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <MenuItem value={""}>All</MenuItem>
                      <MenuItem value={"sent"}>Success</MenuItem>
                      <MenuItem value={"failed"}>Failure</MenuItem>
                    </Select>
                  </FormControl>

                  <Typography sx={{ fontWeight: 700, mb: 1.5 }}>By Recipient Email</Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="Recipient Email"
                    name="email"
                    type="email"
                    autoComplete="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Exact match"
                  />

                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={clearFilters} disabled={!hasFilters} sx={{ textTransform: "none", fontWeight: 600 }}>
                      Clear all filters
                    </Button>
                  </Box>
                </CardContent>
              </Card>

            </Box>
          </AccordionDetails>
        </Accordion>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date/Time</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Template Type</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
                    No logs found for the current filters.
                  </TableCell>
                </TableRow>
              ) : null}
              {logs.map((log) => (
                <TableRow key={log._id} hover>
                  <TableCell>{moment(log.timestamp).format("DD/MM/YYYY HH:mm")}</TableCell>
                  <TableCell>{log.to}</TableCell>
                  <TableCell>{getEmailTemplateTypeLabel(log.templateType)}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(log.status)}
                      size="small"
                      color={getStatusChipColor(log.status)}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[numRows]}
            rowsPerPage={numRows}
            component="div"
            count={totalRows}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
          />
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default EmailLogs;
